import { apiJson } from '../api';
import type { AdminStory, AdminStoryDetail, ArticleChange, ArticleChangeDetail, CuratorOverride, StoryFilterQuery } from '../types';
import type { CuratorOverrideType } from '../utils/curatorOverride';

export async function listStories(opts?: { domain?: string; limit?: number }): Promise<AdminStory[]> {
  const params = new URLSearchParams();
  if (opts?.domain) params.set('domain', opts.domain);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const data = await apiJson<AdminStory[]>(`/api/admin/stories${qs ? `?${qs}` : ''}`);
  return Array.isArray(data) ? data : [];
}

export async function listTopStories(n = 250): Promise<AdminStory[]> {
  const params = new URLSearchParams({ n: String(n) });
  const data = await apiJson<AdminStory[]>(`/api/admin/stories/top?${params.toString()}`);
  return Array.isArray(data) ? data : [];
}

export async function filterStories(query: StoryFilterQuery): Promise<AdminStory[]> {
  const data = await apiJson<AdminStory[]>('/api/admin/stories/filter', {
    method: 'POST',
    json: query,
  });
  return Array.isArray(data) ? data : [];
}

export async function searchStories(q: string, opts?: { field?: string; limit?: number }): Promise<AdminStory[]> {
  const params = new URLSearchParams({ q });
  if (opts?.field) params.set('field', opts.field);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const data = await apiJson<AdminStory[]>(`/api/admin/stories/search?${params.toString()}`);
  return Array.isArray(data) ? data : [];
}

export async function getStory(storyId: string): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}`);
}

// With a url this is a complete server-side ingest: the backend fetches
// content and queues enrichment itself, so no follow-up fetch-content/enrich
// calls are needed. Omit title to let the fetched page title win.
export async function createStory(data: {
  title?: string;
  url?: string;
  tier?: number;
  note?: string;
}): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>('/api/admin/stories', {
    method: 'POST',
    json: data,
  });
}

export async function updateStory(storyId: string, data: {
  title?: string;
  description?: string;
  url?: string;
  linked_url?: string;
}): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}`, {
    method: 'PATCH',
    json: data,
  });
}

export async function fetchStoryContent(storyId: string): Promise<AdminStoryDetail> {
  return apiJson<AdminStoryDetail>(`/api/admin/stories/${encodeURIComponent(storyId)}/fetch-content`, {
    method: 'POST',
  });
}

export async function enrichStory(storyId: string): Promise<{ message: string }> {
  return apiJson<{ message: string }>(`/api/admin/stories/${encodeURIComponent(storyId)}/enrich`, {
    method: 'POST',
  });
}

export async function fetchRecent(ageHours: number): Promise<{ inserted: number; processed_feeds: number; duration_seconds: number }> {
  return apiJson('/api/admin/ingest', {
    method: 'POST',
    json: { age_filter_hours: ageHours },
  });
}

export async function getStoryChanges(storyId: string): Promise<ArticleChange[]> {
  return apiJson<ArticleChange[]>(`/api/admin/stories/${encodeURIComponent(storyId)}/changes`);
}

export async function getChange(changeId: string): Promise<ArticleChangeDetail> {
  return apiJson<ArticleChangeDetail>(`/api/admin/changes/${encodeURIComponent(changeId)}`);
}

export async function submitScoreFeedback(
  articleId: string,
  payload: {
    field: 'tier';
    old_value: number;
    new_value: number;
    comment?: string;
  },
): Promise<void> {
  await apiJson(`/api/admin/articles/${encodeURIComponent(articleId)}/score-feedback`, {
    method: 'POST',
    json: payload,
  });
}

export async function listCuratorOverrides(overrideType?: string): Promise<CuratorOverride[]> {
  const params = new URLSearchParams();
  if (overrideType) params.set('override_type', overrideType);
  const qs = params.toString();
  return apiJson<CuratorOverride[]>(`/api/admin/curator-overrides${qs ? `?${qs}` : ''}`);
}

export async function createCuratorOverride(payload: {
  override_type: CuratorOverrideType;
  article_id: string;
  target_article_id?: string;
  topic?: string;
  old_value?: string;
  new_value?: string;
  note?: string;
}): Promise<CuratorOverride> {
  return apiJson<CuratorOverride>('/api/admin/curator-overrides', {
    method: 'POST',
    json: payload,
  });
}

export async function deleteCuratorOverride(overrideId: string): Promise<void> {
  await apiJson(`/api/admin/curator-overrides/${encodeURIComponent(overrideId)}`, {
    method: 'DELETE',
  });
}

export async function upsertQualityScore(articleId: string, score: number, comment?: string): Promise<CuratorOverride> {
  return apiJson<CuratorOverride>('/api/admin/curator-overrides/quality-score', {
    method: 'POST',
    json: { article_id: articleId, score, comment: comment || undefined },
  });
}

export async function getQualityScoresForArticles(articleIds: string[]): Promise<Record<string, number>> {
  if (articleIds.length === 0) return {};
  return apiJson<Record<string, number>>('/api/admin/curator-overrides/quality-scores-for-articles', {
    method: 'POST',
    json: { article_ids: articleIds },
  });
}

export async function unassignTopic(articleId: string): Promise<void> {
  await apiJson(`/api/admin/articles/${encodeURIComponent(articleId)}/unassign-topic`, {
    method: 'POST',
  });
}

export async function assignTopic(articleId: string, topic: string): Promise<{ topic: string }> {
  return apiJson<{ topic: string }>(`/api/admin/articles/${encodeURIComponent(articleId)}/assign-topic`, {
    method: 'POST',
    json: { topic },
  });
}

export async function deleteQualityScore(articleId: string): Promise<void> {
  await apiJson(`/api/admin/curator-overrides/quality-score/${encodeURIComponent(articleId)}`, {
    method: 'DELETE',
  });
}
